import React from 'react';
import dayjs from 'dayjs';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import { useTranslation } from 'react-i18next';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { IconButton } from '../../../../common/controls/Controls';
import { DownloadOutlined } from '@ant-design/icons';

const normalizeCollection = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const PhdStudentEvaluationsTable = ({
  studentEvaluations,
  onDetailClick,
  onDelete,
  onDownload,
  canDownloadRow,
  isDownloading = false,
  serviceMode = 'evaluation',
  pagination,
  onPaginationChange,
}) => {
  const { t } = useTranslation();
  const isGradesMode = serviceMode === 'grades';
  const permissionsBasic = isGradesMode
    ? 'phd-student-evaluation-grades'
    : 'phd-student-evaluations';
  const uniqueTableId = isGradesMode
    ? 'phd-student-evaluation-grades-table'
    : 'phd-student-evaluations-table';
  const rows = normalizeCollection(studentEvaluations);

  const studentFilter = useDataGridTextFilter('student_display_name');
  const conclusionFilter = useDataGridTextFilter('conclusion_label');
  const updatedByFilter = useDataGridTextFilter('updated_by_name');

  const translateConclusion = (value) => {
    if (!value) return '—';
    if (value === 'Continue') return t('Pokračovať');
    if (value === 'End') return t('Ukončiť');
    if (value === 'Expulsion') return t('Vylúčenie');
    if (value === 'ContinueExtended') return t('Pokračovať s predĺžením');
    return value;
  };

  const columns = isGradesMode
    ? [
        {
          title: t('Študent'),
          dataIndex: 'student_display_name',
          key: 'student_display_name',
          ellipsis: true,
          ...studentFilter,
        },
        {
          title: t('Známky zadané'),
          dataIndex: 'grades_submitted_at',
          key: 'grades_submitted_at',
          ellipsis: true,
          render: (value) =>
            value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
        },
        {
          title: t('Naposledy upravené'),
          dataIndex: 'updated_at',
          key: 'updated_at',
          ellipsis: true,
          render: (value) =>
            value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
        },
        {
          title: t('Upravil'),
          dataIndex: 'updated_by_name',
          key: 'updated_by_name',
          ellipsis: true,
          render: (value) => value || '—',
          ...updatedByFilter,
        },
      ]
    : [
        {
          title: t('Študent'),
          dataIndex: 'student_display_name',
          key: 'student_display_name',
          ellipsis: true,
          ...studentFilter,
        },
        {
          title: t('Záver'),
          dataIndex: 'conclusion_label',
          key: 'conclusion_label',
          ellipsis: true,
          ...conclusionFilter,
        },
        {
          title: t('Naposledy upravené'),
          dataIndex: 'updated_at',
          key: 'updated_at',
          ellipsis: true,
          render: (value) =>
            value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—',
        },
        {
          title: t('Upravil'),
          dataIndex: 'updated_by_name',
          key: 'updated_by_name',
          ellipsis: true,
          render: (value) => value || '—',
          ...updatedByFilter,
        },
      ];

  const customSettings = (row) => (
    <IconButton
      icon={<DownloadOutlined />}
      tooltip={t('Stiahnuť DOCX')}
      disabled={Boolean(
        !onDownload ||
          isDownloading ||
          (canDownloadRow && !canDownloadRow(row)) ||
          (isGradesMode && !row.linked_evaluation_id),
      )}
      disabledTooltip={
        !onDownload
          ? t('Sťahovanie nie je dostupné')
          : canDownloadRow && !canDownloadRow(row)
            ? t('Archívnu verziu môže vydať iba administrátor')
          : isGradesMode && !row.linked_evaluation_id
            ? t('Ročné hodnotenie ešte neexistuje')
            : isDownloading
              ? t('Dokument sa práve generuje')
              : undefined
      }
      onClick={(e) => {
        e.stopPropagation();
        if (
          !onDownload ||
          isDownloading ||
          (canDownloadRow && !canDownloadRow(row))
        ) {
          return;
        }
        onDownload(row);
      }}
    />
  );

  return (
    <DataGrid
      locale={{
        emptyText: t(
          'Pre zvolený pohľad a filtre nie sú dostupné žiadne záznamy.',
        ),
      }}
      rowKey={(evaluation) => evaluation.id}
      columns={columns}
      dataSource={rows.map((evaluation) => ({
        ...evaluation,
        conclusion_label: translateConclusion(evaluation.conclusion),
      }))}
      permissionsBasic={permissionsBasic}
      uniqueTableId={uniqueTableId}
      customSettings={customSettings}
      allowClick={isGradesMode}
      onEditClick={onDetailClick}
      onRowClick={onDetailClick}
      onDelete={onDelete}
      secondaryCondition={(row) => row.can_edit !== false}
      deleteCondition={(row) => row.can_delete !== false}
      editDisabledTooltip={t('Archivovaný záznam je iba na čítanie')}
      deleteDisabledTooltip={t('Archivovaný záznam nie je možné vymazať')}
      pagination={pagination}
      onChange={onPaginationChange}
    />
  );
};

export default PhdStudentEvaluationsTable;
